import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertStatus, AlertQuery } from "../types/alerts";
import { useAlerts, useUpdateAlert, useDeleteAlert } from "@/hooks/useAlerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Play, Pause, MoreVertical, Edit, Trash2, Loader2, Mail, MessageSquare, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AlertsListPage() {
  const navigate = useNavigate();
  const { data: alertsData, isLoading, error } = useAlerts();
  const updateAlert = useUpdateAlert();
  const deleteAlert = useDeleteAlert();

  const alerts = alertsData?.data || [];

  const handleStatusToggle = useCallback(
    async (alertId: number, currentStatus: AlertStatus) => {
      const newStatus = currentStatus === "ACTIVE" ? "PAUSED" : "ACTIVE";
      try {
        await updateAlert.mutateAsync({
          id: alertId,
          data: { status: newStatus },
        });
      } catch (err) {
        console.error("Failed to update alert status:", err);
      }
    },
    [updateAlert]
  );

  const handleDelete = useCallback(
    async (alertId: number) => {
      if (window.confirm("Are you sure you want to delete this alert?")) {
        try {
          await deleteAlert.mutateAsync(alertId);
        } catch (err) {
          console.error("Failed to delete alert:", err);
        }
      }
    },
    [deleteAlert]
  );

  const statusVariants = useMemo(
    () =>
      ({
        ACTIVE: { variant: "default" as const, label: "Active" },
        PAUSED: { variant: "secondary" as const, label: "Paused" },
        INACTIVE: { variant: "outline" as const, label: "Inactive" },
      }) as const,
    []
  );

  const getStatusBadge = useCallback(
    (status: AlertStatus) => {
      const { variant, label } = statusVariants[status];
      return (
        <Badge variant={variant} className="rounded-[5px] text-xs">
          {label}
        </Badge>
      );
    },
    [statusVariants]
  );

  const formatDate = useCallback((dateString: string | null | undefined) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  }, []);

  const formatQuerySummary = useCallback((query: AlertQuery | null | undefined): string => {
    if (!query) return "No query";
    const parts: string[] = [];
    if (query.tags?.values?.length) {
      parts.push(`Tags: ${query.tags.values.join(", ")}`);
    }
    if (query.metadata?.length) {
      const metadataParts = query.metadata
        .map((m) => (m.path ? m.path.split(".").pop() : ""))
        .filter(Boolean);
      if (metadataParts.length) {
        parts.push(`Metadata: ${metadataParts.join(", ")}`);
      }
    }
    if (query.raw_data) {
      parts.push("Raw data filter");
    }
    return parts.length > 0 ? parts.join(" | ") : "Custom query";
  }, []);

  const renderChannels = useCallback((channels: Alert["channels"]) => {
    if (!channels) return <span className="text-muted-foreground text-xs">None</span>;
    
    const channelIcons: JSX.Element[] = [];
    
    if (channels.email) {
      channelIcons.push(
        <span key="email" className="inline-flex items-center gap-1 text-xs" title="Email">
          <Mail className="h-3 w-3" />
          <span className="sr-only">Email</span>
        </span>
      );
    }
    
    if (channels.slack) {
      channelIcons.push(
        <span key="slack" className="inline-flex items-center gap-1 text-xs" title="Slack">
          <MessageSquare className="h-3 w-3" />
          <span className="sr-only">Slack</span>
        </span>
      );
    }
    
    if (channels.teams) {
      channelIcons.push(
        <span key="teams" className="inline-flex items-center gap-1 text-xs" title="Microsoft Teams">
          <Users className="h-3 w-3 text-blue-600" />
          <span className="sr-only">Teams</span>
        </span>
      );
    }
    
    if (channelIcons.length === 0) {
      return <span className="text-muted-foreground text-xs">None</span>;
    }
    
    return <div className="flex items-center gap-2">{channelIcons}</div>;
  }, []);

  return (
    <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium">Alerts & Policies</h1>
          <p className="text-sm text-muted-foreground">
            Manage your AI security alerts and monitoring policies
          </p>
        </div>
        <Button className="rounded-full" onClick={() => navigate("/alerts/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Create Alert
        </Button>
      </div>

      <Card className="overflow-hidden rounded-[15px] shadow-none">
        <CardHeader>
          <CardTitle className="text-base font-medium">
            Alerts {alertsData ? `(${alertsData.total_count})` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading alerts...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Failed to load alerts. Please try again.</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No alerts configured. Create your first alert to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Checked</TableHead>
                  <TableHead>Trigger Count</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((alert: Alert) => (
                  <TableRow
                    key={alert.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/alerts/${alert.id}/edit`)}
                  >
                    <TableCell className="font-medium">{alert.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          alert.priority === "P0"
                            ? "default"
                            : alert.priority === "P1"
                              ? "default"
                              : "secondary"
                        }
                        className={`rounded-[5px] text-xs ${
                          alert.priority === "P0"
                            ? "bg-red-500 hover:bg-red-600"
                            : alert.priority === "P1"
                              ? "bg-orange-500 hover:bg-orange-600"
                              : ""
                        }`}
                      >
                        {alert.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md">
                      <p className="text-sm text-muted-foreground truncate" title={JSON.stringify(alert.query)}>
                        {formatQuerySummary(alert.query)}
                      </p>
                    </TableCell>
                    <TableCell>{renderChannels(alert.channels)}</TableCell>
                    <TableCell>{getStatusBadge(alert.status)}</TableCell>
                    <TableCell className="text-sm">{formatDate(alert.last_checked)}</TableCell>
                    <TableCell className="text-sm">{alert.triggerCount}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-[20px] bg-white p-2">
                          <DropdownMenuItem onClick={() => navigate(`/alerts/${alert.id}/edit`)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusToggle(alert.id, alert.status);
                            }}
                            disabled={updateAlert.isPending}
                          >
                            {alert.status === "ACTIVE" ? (
                              <>
                                <Pause className="mr-2 h-4 w-4" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-4 w-4" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(alert.id);
                            }}
                            className="text-destructive"
                            disabled={deleteAlert.isPending}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
